import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
    limit
} from 'firebase/firestore';
import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, storage } from './config';

export interface ProjectFeature {
    name: string;
    subFeatures: string[];
}

export interface Project {
    id?: string;
    title: string;
    description: string;
    longDescription: string;
    image: string;
    gallery?: string[];
    tags: string[];
    technologies: string[];
    features: ProjectFeature[]; // New structured features
    legacyFeatures?: string[]; // Keep old simple features for backward compatibility
    categories: string[]; // Changed from category: string to categories: string[]
    category?: string; // Keep for backward compatibility with existing projects
    link?: string;
    github?: string;
    company?: string; // Company name where this project was developed
    companyWebsite?: string; // Optional company website URL
    projectType?: 'personal' | 'company' | 'freelance' | 'open-source'; // Type of project
    employmentType?: 'full-time' | 'part-time' | 'contract' | 'internship'; // Employment type for company projects
    featured?: boolean;
    order?: number; // Add order field for reordering
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Collection reference
const projectsCollection = collection(db, 'projects');

/**
 * Get all projects
 */
export const getAllProjects = async (): Promise<Project[]> => {
    try {
        // Get all projects first without any ordering constraints
        const querySnapshot = await getDocs(projectsCollection);

        const projects = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Project));

        // Sort projects: first by order if it exists, then by createdAt
        projects.sort((a, b) => {
            // If both have order, sort by order
            if (typeof a.order === 'number' && typeof b.order === 'number') {
                return a.order - b.order;
            }
            // If only one has order, prioritize it
            if (typeof a.order === 'number') return -1;
            if (typeof b.order === 'number') return 1;

            // If neither has order, sort by createdAt (newest first)
            if (a.createdAt && b.createdAt) {
                return b.createdAt.toMillis() - a.createdAt.toMillis();
            }
            return 0;
        });

        return projects;
    } catch (error) {
        console.error('Error getting projects:', error);
        throw error;
    }
};

/**
 * Get projects by category (supports both new categories array and legacy category field)
 */
export const getProjectsByCategory = async (category: string): Promise<Project[]> => {
    try {
        // Get all projects and filter in JavaScript for better flexibility
        // This approach works with both legacy category field and new categories array
        const projectsQuery = query(projectsCollection, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(projectsQuery);

        const allProjects = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Project));

        // Filter projects that have the category in either categories array or legacy category field
        return allProjects.filter(project => {
            if (project.categories) {
                return project.categories.includes(category);
            }
            // Fallback to legacy category field
            return project.category === category;
        });
    } catch (error) {
        console.error('Error getting projects by category:', error);
        throw error;
    }
};

/**
 * Helper function to get all unique categories from projects
 */
export const getAllProjectCategories = async (): Promise<string[]> => {
    try {
        const projectsQuery = query(projectsCollection);
        const querySnapshot = await getDocs(projectsQuery);

        const allProjects = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Project));

        const categoriesSet = new Set<string>();

        allProjects.forEach(project => {
            // Handle new categories array
            if (project.categories && Array.isArray(project.categories)) {
                project.categories.forEach(cat => categoriesSet.add(cat));
            }
            // Handle legacy category field
            if (project.category && typeof project.category === 'string') {
                categoriesSet.add(project.category);
            }
        });

        return Array.from(categoriesSet).sort();
    } catch (error) {
        console.error('Error getting all project categories:', error);
        throw error;
    }
};

/**
 * Get a project by ID
 */
export const getProjectById = async (id: string): Promise<Project | null> => {
    try {
        const docRef = doc(db, 'projects', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Project;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting project:', error);
        throw error;
    }
};

/**
 * Create a new project
 */
export const createProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        // Get current projects to determine the next order
        const existingProjects = await getAllProjects();
        const nextOrder = existingProjects.length;

        const newProject: Omit<Project, 'id'> = {
            ...projectData,
            order: nextOrder,
            createdAt: serverTimestamp() as Timestamp,
            updatedAt: serverTimestamp() as Timestamp
        };

        const docRef = await addDoc(projectsCollection, newProject);
        return docRef.id;
    } catch (error) {
        console.error('Error creating project:', error);
        throw error;
    }
};

/**
 * Update a project
 */
export const updateProject = async (id: string, projectData: Partial<Omit<Project, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        const docRef = doc(db, 'projects', id);
        await updateDoc(docRef, {
            ...projectData,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating project:', error);
        throw error;
    }
};

/**
 * Delete a project
 */
export const deleteProject = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'projects', id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting project:', error);
        throw error;
    }
};

/**
 * Upload a project image to storage
 */
export const uploadProjectImage = async (file: File, projectId: string, isGallery: boolean = false, fileName?: string): Promise<string> => {
    try {
        const timestamp = new Date().getTime();
        const name = fileName || `${timestamp}_${file.name}`;
        const path = isGallery
            ? `projects/${projectId}/gallery/${name}`
            : `projects/${projectId}/${name}`;

        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    // Progress monitoring if needed
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log('Upload is ' + progress + '% done');
                },
                (error) => {
                    // Error handling
                    console.error('Error uploading image:', error);
                    reject(error);
                },
                async () => {
                    // Upload complete
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                }
            );
        });
    } catch (error) {
        console.error('Error in upload function:', error);
        throw error;
    }
};

/**
 * Delete a project image from storage
 */
export const deleteProjectImage = async (imageUrl: string): Promise<void> => {
    try {
        // Extract the path from the URL
        const storageRef = ref(storage, imageUrl);
        await deleteObject(storageRef);
    } catch (error) {
        console.error('Error deleting image:', error);
        throw error;
    }
};

/**
 * Get featured projects ordered by their order field, then by createdAt
 */
export const getFeaturedProjects = async (): Promise<Project[]> => {
    try {
        console.log("Attempting to fetch featured projects...");

        // Get all projects first, then filter and sort client-side
        // This avoids index issues and ensures consistent ordering with order field
        const allProjects = await getAllProjects();

        // Filter for featured projects
        const featuredProjects = allProjects.filter(project => project.featured === true);

        console.log(`Found ${featuredProjects.length} featured projects`);

        // If featured projects found, return them (already sorted by getAllProjects)
        if (featuredProjects.length > 0) {
            return featuredProjects;
        }

        // Fallback: get recent projects if no featured projects exist
        console.log("No featured projects found, falling back to recent projects");
        return allProjects.slice(0, 3);

    } catch (error) {
        console.error('Error getting featured projects:', error);
        if (error instanceof Error) {
            console.error('Error details:', error.message, error.stack);
        }
        throw error;
    }
};

/**
 * Move project up in order
 */
export const moveProjectUp = async (id: string): Promise<void> => {
    try {
        const projects = await getAllProjects();
        const index = projects.findIndex(proj => proj.id === id);

        if (index <= 0) return; // Already at the top

        // Swap with previous project
        const currentProj = projects[index];
        const prevProj = projects[index - 1];

        if (currentProj.id && prevProj.id) {
            await updateProject(currentProj.id, { order: index - 1 });
            await updateProject(prevProj.id, { order: index });
        }
    } catch (error) {
        console.error('Error moving project up:', error);
        throw error;
    }
};

/**
 * Move project down in order
 */
export const moveProjectDown = async (id: string): Promise<void> => {
    try {
        const projects = await getAllProjects();
        const index = projects.findIndex(proj => proj.id === id);

        if (index === -1 || index >= projects.length - 1) return; // Already at the bottom

        // Swap with next project
        const currentProj = projects[index];
        const nextProj = projects[index + 1];

        if (currentProj.id && nextProj.id) {
            await updateProject(currentProj.id, { order: index + 1 });
            await updateProject(nextProj.id, { order: index });
        }
    } catch (error) {
        console.error('Error moving project down:', error);
        throw error;
    }
};

/**
 * Initialize order for existing projects that don't have an order field
 */
export const initializeProjectOrder = async (): Promise<void> => {
    try {
        // Get all projects without order filtering
        const projectsQuery = query(projectsCollection, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(projectsQuery);

        const projects = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Project));

        // Update projects that don't have an order field
        const updatePromises = projects.map(async (project, index) => {
            if (project.id && typeof project.order === 'undefined') {
                await updateProject(project.id, { order: index });
            }
        });

        await Promise.all(updatePromises);
        console.log('Project order initialization completed');
    } catch (error) {
        console.error('Error initializing project order:', error);
        throw error;
    }
};

